import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import useAuthTransition from '@aurora/shared-client/components/authentication/useAuthTransition';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import useCommunityPolicies from '@aurora/shared-client/components/community/useCommunityPolicies';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import useUserManagementProperties from '@aurora/shared-client/components/users/useUserManagementProperties';
import {
  canAccessPageBuilder as checkCanAccessPageBuilder,
  canReportAbuseDSA
} from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import Icons from '@aurora/shared-client/icons';
import type {
  ManageUsersPageAndParams,
  PageAndEmptyParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { ContextUserFragment } from '@aurora/shared-generated/types/graphql-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import IdConverter from '@aurora/shared-utils/graphql/IdConverter/IdConverter';
import { dispatchUserUpdateEvent } from '@aurora/shared-utils/helpers/events/EventHelper';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import { AuthTransitionType, UserUpdateType } from '@aurora/shared-utils/types/enums';
import dynamic from 'next/dynamic';
import React, { useContext, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import endUserComponentRegistry from '../../../features/endUserComponentRegistry';
import type {
  AdminNotePolicyQuery,
  AdminNotePolicyQueryVariables,
  BanUserPolicyQuery,
  BanUserPolicyQueryVariables,
  CanCloseAccountPoliciesQuery,
  CanCloseAccountPoliciesQueryVariables,
  SetUserEmailVerifiedMutation,
  SetUserEmailVerifiedMutationVariables,
  UserStatusQuery,
  UserStatusQueryVariables,
  UserViewQuery,
  UserViewQueryVariables
} from '../../../types/graphql-types';
import banUserPolicyQuery from '../../bans/BanUserPolicy.query.graphql';
import useStatusQuery from '../../bans/UserStatus.query.graphql';
import ActionFeedbacks from '../../common/ActionFeedback/ActionFeedbackTypes';
import { usePageEditorLink } from '../../usePageEditorLink';
import useTranslation from '../../useTranslation';
import adminNotePolicy from '../AdminNotesModal/AdminNotePolicy.query.graphql';
import ManageUserRoleModal from '../ManageUserRoleModal/ManageUserRoleModal';
import ReportUserAction from '../ReportUserAction/ReportUserAction';
import UserProfileActionBanMember from '../UserProfileActionBanMember/UserProfileActionBanMember';
import userQuery from '../UserView.query.graphql';
import canCloseAccountPoliciesQuery from './../CloseUserAccount/CanCloseAccountPolicies.query.graphql';
import { UserProfileAction } from './enums';
import setUserEmailVerifiedMutation from './SetUserEmailVerified.mutation.graphql';
import localStyles from './UserProfileActionMenu.module.pcss';

const AddAdminNoteModal = dynamic(
  () => import('../AdminNotesModal/AddAdminNoteModal/AddAdminNoteModal'),
  {
    ssr: false
  }
);

const AdminNotesModal = dynamic(() => import('../AdminNotesModal/AdminNotesModal'), {
  ssr: false
});

const CloseUserAccountModal = dynamic(
  () => import('../CloseUserAccount/CloseUserAccountModal/CloseUserAccountModal'),
  { ssr: false }
);

const log = getLog(module);

interface Props {
  /**
   * The user to whom the profile page belongs.
   */
  contextUser: ContextUserFragment;
}

interface UserProfileActionMenuItem {
  /**
   * Name of the action
   */
  name: UserProfileAction;
  /**
   * Method to determine whether to display the action item
   *
   * @return whether or not to display the action item.
   */
  policy?: () => boolean;
  /**
   * Event handler for the action taken
   */
  actionHandler: () => void;
}

/**
 * Display action menu for user settings
 *
 * @constructor
 *
 * @author Manasvini Arul
 */
const UserProfileActionMenu: React.FC<React.PropsWithChildren<Props>> = ({ contextUser }) => {
  const uidSeed = useUIDSeed();
  const { addToast } = useToasts();
  const cx = useClassNameMapper(localStyles);
  const handleAuthTransition = useAuthTransition();
  const { router, Link } = useEndUserRoutes();
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_PROFILE_ACTION_MENU
  );
  const { isEditable: isPageEditable } = endUserComponentRegistry.getPageDescriptor(
    EndUserPages.UserPage
  );
  const client = useApolloClient();
  const { link: pageEditorLink } = usePageEditorLink();
  const [setUserEmailVerified] = useMutationWithTracing<
    SetUserEmailVerifiedMutation,
    SetUserEmailVerifiedMutationVariables
  >(module, setUserEmailVerifiedMutation);
  const { isAnonymous } = useRegistrationStatus();

  const { data: userManagementData, loading: userManagementLoading } =
    useUserManagementProperties();

  const { data: userData, loading: userLoading } = useQueryWithTracing<
    UserViewQuery,
    UserViewQueryVariables
  >(module, userQuery, {
    variables: {
      id: contextUser?.id,
      useRegistrationData: true
    },
    skip: isAnonymous
  });

  const { data: banUserData, loading: banUserLoading } = useQueryWithTracing<
    UserStatusQuery,
    UserStatusQueryVariables
  >(module, useStatusQuery, {
    variables: {
      id: contextUser?.id
    }
  });

  //For closing the current profile's account
  const [showCloseUserAccountModal, setShowCloseUserAccountModal] = useState<boolean>(false);
  const [showAdminNotes, setShowAdminNotes] = useState<boolean>(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState<boolean>(false);
  const [showBanModal, setShowBanModal] = useState<boolean>(false);
  const [showManageRoleModal, setShowManageRoleModal] = useState<boolean>(false);

  const profileUserId = contextUser?.id;
  const { data: canClosePolicyData, loading: canClosePolicyLoading } = useQueryWithTracing<
    CanCloseAccountPoliciesQuery,
    CanCloseAccountPoliciesQueryVariables
  >(module, canCloseAccountPoliciesQuery, {
    variables: {
      id: profileUserId
    }
  });

  const { data: adminPolicyData, loading: adminPolicyDataLoading } = useQueryWithTracing<
    AdminNotePolicyQuery,
    AdminNotePolicyQueryVariables
  >(module, adminNotePolicy);

  const { data: communityPoliciesData, loading: communityPoliciesLoading } = useCommunityPolicies(
    module,
    {
      useCanAccessPageBuilder: true,
      useCanReportAbuseDSA: true
    }
  );
  const canAccessPageBuilder: boolean = checkCanAccessPageBuilder(communityPoliciesData?.coreNode);
  const showAdminMenu = isPageEditable && canAccessPageBuilder;

  const { data: banPoliciesData, loading: banPoliciesLoading } = useQueryWithTracing<
    BanUserPolicyQuery,
    BanUserPolicyQueryVariables
  >(module, banUserPolicyQuery);

  const { authUser } = useContext(AppContext);
  const isOwnProfilePage = contextUser.id === authUser?.id;
  const canCloseAccount =
    !isOwnProfilePage && checkPolicy(canClosePolicyData?.user?.userPolicies?.canCloseAccount);

  /**
   * This function handles the hide event of the close account modal
   */
  async function handleCloseAccountModalHide() {
    setShowCloseUserAccountModal(false);
  }

  /**
   * This function handles the account closed event of the close account modal
   * @param username The username of the user whose account has been closed
   */
  async function handleAccountClosed(username: string) {
    setShowCloseUserAccountModal(false);
    await router
      .replaceRoute<PageAndEmptyParams>(EndUserPages.CommunityPage, null, {
        [EndUserQueryParams.ACTION_FEEDBACK]: ActionFeedbacks.ACCOUNT_CLOSED_FOR_USER,
        [EndUserQueryParams.USER_NAME]: username,
        [EndUserQueryParams.TIMESTAMP]: Date.now().toString()
      })
      .then(() => {
        return client.cache.evict({ id: `User:${contextUser.id}`, broadcast: false });
      });
    handleAuthTransition(AuthTransitionType.CLOSE_ACCOUNT);
  }

  const renderCloseUserAccountModal = (): React.ReactElement => {
    return (
      <CloseUserAccountModal
        show={showCloseUserAccountModal}
        onHide={handleCloseAccountModalHide}
        onAccountClosed={handleAccountClosed}
        contextUser={contextUser}
      />
    );
  };

  const renderAdminNotesModal = (): React.ReactElement => {
    return (
      <AdminNotesModal
        show={showAdminNotes}
        onHide={() => setShowAdminNotes(false)}
        onShowAddNoteModal={(onShowModal: boolean) => {
          setShowAddNoteModal(onShowModal);
        }}
        onShowAdminNotes={(onShowModal: boolean) => {
          setShowAdminNotes(onShowModal);
        }}
      />
    );
  };

  const renderBanModal = (): React.ReactElement => {
    return <UserProfileActionBanMember show={showBanModal} onHide={() => setShowBanModal(false)} />;
  };

  /**
   * Renders the modal to manage user's role
   *
   * @returns React Element
   */
  function renderManageRoleModal(): React.ReactElement {
    return (
      <ManageUserRoleModal
        show={showManageRoleModal}
        onHide={() => setShowManageRoleModal(false)}
      />
    );
  }

  if (
    textLoading ||
    userLoading ||
    userManagementLoading ||
    canClosePolicyLoading ||
    adminPolicyDataLoading ||
    communityPoliciesLoading ||
    banPoliciesLoading ||
    banUserLoading
  ) {
    return null;
  }

  const canManageUsers =
    userManagementData?.community.userManagementProperties.canManageUsers.failureReason === null;

  const canShowUserReportAbuse = canReportAbuseDSA(communityPoliciesData?.coreNode);
  const showReportMemberOption = !isOwnProfilePage && canShowUserReportAbuse;

  /**
   * Renders toast banner or flyout
   *
   * @param alertVariant toast alert variant.
   * @param titleKey toast title.
   * @param messageKey toast message.
   */
  function renderToast(
    alertVariant: ToastAlertVariant,
    titleKey: string,
    messageKey: string
  ): void {
    const id = uidSeed(`set-user-email-verified-${titleKey}`);
    const toastVariant =
      alertVariant === ToastAlertVariant.DANGER ? ToastVariant.BANNER : ToastVariant.FLYOUT;
    const toastProps: ToastProps = {
      id,
      toastVariant,
      alertVariant,
      title: formatMessage(`setUserEmailVerified.${titleKey}`),
      autohide: true,
      message: formatMessage(`setUserEmailVerified.${messageKey}`),
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Update the principal user cache after setUserEmailVerified mutation call
   *
   * @param cache The apollo cache
   */
  function updateContextUserCache(cache: ApolloCache<{}>) {
    const { user } = cache.readQuery<UserViewQuery, UserViewQueryVariables>({
      query: userQuery,
      variables: {
        id: contextUser.id,
        useRegistrationData: true
      }
    });

    const data = {
      ...user,
      registrationData: {
        ...user.registrationData,
        confirmEmailStatus: true
      }
    };

    async function doUpdate(updatedData): Promise<void> {
      cache.writeQuery<UserViewQuery, UserViewQueryVariables>({
        query: userQuery,
        data: {
          user: updatedData
        },
        variables: {
          id: contextUser.id,
          useRegistrationData: true
        }
      });
    }

    doUpdate(data);
    // return a rollback function
    return (): void => {
      doUpdate(data);
      doUpdate(user);
    };
  }

  /**
   * Event handler for set user email as verified action
   */
  async function setUserEmailVerifiedHandler(): Promise<void> {
    let rollback;
    const mutationPayload: SetUserEmailVerifiedMutationVariables = {
      id: contextUser.id
    };
    const { errors, data: response } = await setUserEmailVerified({
      variables: mutationPayload,
      update: (cache, { errors: updateErrors }): void => {
        if (!updateErrors?.length) {
          rollback = updateContextUserCache(cache);
        }
      }
    });

    if (errors?.length > 0 || response.setUserEmailVerified.errors?.length > 0) {
      log.error('Error while changing email: %O', errors);
      if (rollback) {
        rollback();
      }
      renderToast(ToastAlertVariant.DANGER, 'errorTitle', 'errorMessage');
      return;
    }
    dispatchUserUpdateEvent(UserUpdateType.EMAIL_VERIFIED, authUser);
    renderToast(ToastAlertVariant.SUCCESS, 'successTitle', 'successMessage');
  }

  const actions: UserProfileActionMenuItem[] = [
    {
      name: UserProfileAction.ADMIN_NOTES,
      policy: (): boolean =>
        checkPolicy(adminPolicyData?.self.userPolicies.canManageUser) &&
        authUser.id !== contextUser.id,
      actionHandler: () => setShowAdminNotes(true)
    },
    {
      name: UserProfileAction.MANAGE_ROLES,
      policy: (): boolean => canManageUsers && authUser.id !== contextUser.id,
      actionHandler: () => setShowManageRoleModal(true)
    },
    {
      name: UserProfileAction.SET_EMAIL_AS_VERIFIED,
      policy: (): boolean =>
        !isOwnProfilePage && canManageUsers && !userData?.user.registrationData.confirmEmailStatus,
      actionHandler: setUserEmailVerifiedHandler
    },
    {
      name: UserProfileAction.BAN_MEMBER,
      policy: (): boolean =>
        checkPolicy(banPoliciesData?.self?.userPolicies?.canBanUsers) &&
        !isOwnProfilePage &&
        !banUserData?.user?.banned &&
        !banUserData?.user?.deleted,
      actionHandler: () => setShowBanModal(true)
    }
  ];

  /**
   * Function to check if the action item is to be displayed
   *
   * @param policy A callback function to determine if action item is to be displayed or not
   * @return boolean Whether to display the action item
   */
  function actionShouldDisplay(policy: () => boolean): boolean {
    if (policy) {
      return policy();
    }
    // If no policy provided, display the action item
    return true;
  }

  const hasItemsToDisplay: boolean = actions.some(action => actionShouldDisplay(action?.policy));

  /**
   * Function to render the menu items in the Action menu
   */
  function renderMenuItem(action: UserProfileActionMenuItem): React.ReactElement {
    const displayMenuItem = actionShouldDisplay(action?.policy);
    if (displayMenuItem) {
      return (
        <Dropdown.Item
          key={action.name}
          onClick={action.actionHandler}
          className={cx('lia-dropdown-item')}
          data-testid={`UserProfileActionMenuItem.${action.name}`}
        >
          {formatMessage(`userSettings.${action.name}`)}
        </Dropdown.Item>
      );
    }
    return null;
  }

  /**
   * Renders the edit page link in User Profile Action Menu
   */
  function renderEditPageLink(): React.ReactElement {
    return pageEditorLink(
      <Dropdown.Item
        key={'editPage'}
        className={cx('lia-dropdown-item')}
        data-testid={'UserProfileActionMenuItem.EditPage'}
      >
        {formatMessage('editPage')}
      </Dropdown.Item>
    );
  }

  /**
   * Renders the modal to add admin notes
   */
  function renderAddNoteModal(): React.ReactElement {
    return (
      <>
        <AddAdminNoteModal
          show={showAddNoteModal}
          onHide={() => {
            setShowAddNoteModal(false);
          }}
          onShowAdminNotesModal={(onShowModal: boolean) => {
            setShowAdminNotes(onShowModal);
          }}
        />
      </>
    );
  }

  return (
    (hasItemsToDisplay || showAdminMenu || canCloseAccount || showReportMemberOption) && (
      <>
        <Dropdown
          className={cx('ml-auto')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            as={Button}
            id="lia-user-profile-action-menu"
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              testId="UserProfileActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-user-profile-action-menu"
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="UserProfileActionMenuItem.dropdown"
            className={cx('lia-dropdown-menu-index')}
          >
            {showReportMemberOption && <ReportUserAction contextUser={contextUser} />}
            {hasItemsToDisplay && showReportMemberOption && (
              <Dropdown.Divider data-testid="UserProfileActionMenuItem.ReportUserDivider" />
            )}
            {hasItemsToDisplay && actions.map(action => renderMenuItem(action))}
            {banUserData?.user?.banned && canManageUsers && (
              <Link<ManageUsersPageAndParams>
                route={EndUserPages.ManageUsersPage}
                query={{
                  [EndUserQueryParams.BANNED_USER]: IdConverter.idToTuple(banUserData?.user.id).id,
                  [EndUserQueryParams.FILTER_OPEN]: 'true'
                }}
                passHref
              >
                <Dropdown.Item className={cx('lia-item')}>{formatMessage('editBan')}</Dropdown.Item>
              </Link>
            )}
            {hasItemsToDisplay && showAdminMenu && <Dropdown.Divider />}
            {showAdminMenu && renderEditPageLink()}
            {(hasItemsToDisplay || showAdminMenu) && canCloseAccount && (
              <Dropdown.Divider data-testid="UserProfileActionMenuItem.CloseAccountDivider" />
            )}
            {canCloseAccount && (
              <Dropdown.Item
                key={'closeAccount'}
                className={cx('lia-dropdown-item text-danger')}
                data-testid="UserProfileActionMenuItem.CloseAccount"
                onClick={(): void => setShowCloseUserAccountModal(true)}
              >
                {formatMessage('closeAccount')}
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
        {renderCloseUserAccountModal()}
        {showAdminNotes && renderAdminNotesModal()}
        {showAddNoteModal && renderAddNoteModal()}
        {showBanModal && renderBanModal()}
        {showManageRoleModal && renderManageRoleModal()}
      </>
    )
  );
};

export default UserProfileActionMenu;
